import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Breadcrumb,
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Row,
  Slider,
  Switch,
  message,
} from 'antd';
import {
  CheckOutlined,
  CloseOutlined,
  HomeOutlined,
  SaveOutlined,
  UndoOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import SEO from './SEO';
import {
  colourPalette,
  formItemLayout,
  notificationTimeout,
  notificationType,
  siteConfigLimits,
} from '../common/constants';
import debugLog from '../common/debugLog';
import validateString from '../common/validator';
import {
  firGetSiteConfiguration,
  firUpdateSiteConfiguration,
} from '../firebase';
import './style.scss';

// TODO: use the form properly (follow antd)
export default function Settings({ site }) {
  const [buttonEnabled, setButtonEnabled] = useState(false);
  const [configSite, setConfigSite] = useState({
    householdTenantLimit: 0,
    kioskButtonCooldown: 0,
    kioskNotificationGracePeriod: 0,
    tenantAccountApprove: true,
    useTwilioNotification: false,
    visitorMaxDays: 0,
  });
  const [configUser, setConfigUser] = useState({
    householdTenantLimit: 0,
    kioskButtonCooldown: 0,
    kioskNotificationGracePeriod: 0,
    tenantAccountApprove: true,
    useTwilioNotification: false,
    visitorMaxDays: 0,
  });
  const [loading, setLoading] = useState(false);

  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  // destructure imports
  const { cplNormal } = colourPalette;
  const { colSpanLabel, colSpanNumber, colSpanSlider, colSpanWrapper } =
    formItemLayout;
  const { timeoutError, timeoutSuccess } = notificationTimeout;
  const { typeError, typeSuccess } = notificationType;
  const { id } = site;
  const {
    householdTenantLimitMax,
    householdTenantLimitMin,
    kioskButtonCooldownMax,
    kioskButtonCooldownMin,
    kioskNotificationGracePeriodMax,
    kioskNotificationGracePeriodMin,
    visitorMaxDaysMax,
    visitorMaxDaysMin,
  } = siteConfigLimits;

  // formatter for slider tooltip
  const formatter = (value) => <code className="mono">{value}</code>;

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  const fetchSiteConfiguration = useCallback(
    (siteId) => {
      if (validateString(siteId) === false) {
        console.warn('-- Site ID invalid');
        return;
      }

      setLoading(true);

      firGetSiteConfiguration(siteId)
        .then((siteConfig) => {
          setConfigSite(siteConfig);
          setConfigUser(siteConfig);
          setLoading(false);
          debugLog('-- Fetched site configuration');
          openMessage(
            'Fetched site configuration',
            timeoutSuccess,
            typeSuccess,
          );
        })
        .catch((error) => {
          setConfigSite({
            householdTenantLimit: 0,
            kioskButtonCooldown: 0,
            kioskNotificationGracePeriod: 0,
            tenantAccountApprove: true,
            useTwilioNotification: false,
            visitorMaxDays: 0,
          });
          setConfigUser({
            householdTenantLimit: 0,
            kioskButtonCooldown: 0,
            kioskNotificationGracePeriod: 0,
            tenantAccountApprove: true,
            useTwilioNotification: false,
            visitorMaxDays: 0,
          });
          setLoading(false);
          console.error('-- Error fetching site configuration', error);
          openMessage(
            'Error fetching site configuration',
            timeoutError,
            typeError,
          );
        });
    },
    [
      openMessage,
      timeoutError,
      timeoutSuccess,
      typeError,
      typeSuccess,
    ],
  );

  const handleReset = () => {
    setConfigUser(configSite);
    setButtonEnabled(false);
    debugLog('-- Form reset');
  };

  // rev 109 (2026-05-18) — pre-rev-109 the success toast fired even on
  // permission-denied writes. Fixed in firUpdateSiteConfiguration (it now
  // awaits + propagates errors); the .catch below handles the toast.
  const updateSiteConfiguration = () => {
    firUpdateSiteConfiguration(id, configUser)
      .then(() => {
        setConfigSite(configUser);
        setButtonEnabled(false);
        debugLog('-- Site configuration saved');
        openMessage('Site configuration saved', timeoutSuccess, typeSuccess);
      })
      .catch((error) => {
        console.error('-- Error saving document', error);
        openMessage('Error saving document', timeoutError, typeError);
      });
  };

  useEffect(() => fetchSiteConfiguration(id), [fetchSiteConfiguration, id]);

  return (
    <>
      <SEO
        description="Adjust site settings"
        name="qiwei"
        title="Settings"
        type="webapp"
      />
      <div>
        <Breadcrumb
          items={[
            {
              title: (
                <Link to="..">
                  <HomeOutlined className="dropdown-icon-margin" />
                  <span>Home</span>
                </Link>
              ),
            },
            {
              title: <span>Settings</span>,
            },
          ]}
        />
      </div>
      {contextHolder}
      <Card
        actions={[
          <Button
            disabled={!buttonEnabled}
            icon={<SaveOutlined />}
            loading={loading}
            onClick={form.submit}
            type="link"
          >
            Save Changes
          </Button>,
          <Button
            danger
            icon={<UndoOutlined />}
            onClick={handleReset}
            type="link"
          >
            Reset
          </Button>,
        ]}
        className="setting card"
        hoverable
        loading={loading}
        title="Site Settings"
      >
        <Form
          className="setting form"
          colon={false}
          form={form}
          id="settings"
          labelCol={{ span: colSpanLabel }}
          labelWrap
          name="settings"
          onFinish={updateSiteConfiguration}
          scrollToFirstError
          variant="filled"
          wrapperCol={{ span: colSpanWrapper }}
        >
          <Form.Item
            id="householdTenantLimit"
            label={`Household Tenant Limit (${householdTenantLimitMin} - ${householdTenantLimitMax})`}
            name="householdTenantLimit"
          >
            <Row>
              <Col span={colSpanSlider}>
                <Slider
                  max={householdTenantLimitMax}
                  min={householdTenantLimitMin}
                  onChange={(value) => {
                    setButtonEnabled(true);
                    setConfigUser((prev) => ({
                      ...prev,
                      householdTenantLimit: value,
                    }));
                  }}
                  tooltip={{ color: cplNormal, formatter }}
                  value={
                    typeof configUser.householdTenantLimit === 'number'
                      ? configUser.householdTenantLimit
                      : 0
                  }
                />
              </Col>
              <Col span={colSpanNumber}>
                <InputNumber
                  changeOnWheel
                  className="setting form number"
                  controls
                  max={householdTenantLimitMax}
                  min={householdTenantLimitMin}
                  onChange={(value) => {
                    setButtonEnabled(true);
                    setConfigUser((prev) => ({
                      ...prev,
                      householdTenantLimit: value,
                    }));
                  }}
                  value={configUser.householdTenantLimit}
                />
              </Col>
            </Row>
          </Form.Item>
          <Form.Item
            id="kioskButtonCooldown"
            label="Kiosk Button Cooldown (seconds)"
            name="kioskButtonCooldown"
          >
            <Row>
              <Col span={colSpanSlider}>
                <Slider
                  max={kioskButtonCooldownMax}
                  min={kioskButtonCooldownMin}
                  onChange={(value) => {
                    setButtonEnabled(true);
                    setConfigUser((prev) => ({
                      ...prev,
                      kioskButtonCooldown: value,
                    }));
                  }}
                  tooltip={{ color: cplNormal, formatter }}
                  value={
                    typeof configUser.kioskButtonCooldown === 'number'
                      ? configUser.kioskButtonCooldown
                      : 0
                  }
                />
              </Col>
              <Col span={colSpanNumber}>
                <InputNumber
                  changeOnWheel
                  className="setting form number"
                  controls
                  max={kioskButtonCooldownMax}
                  min={kioskButtonCooldownMin}
                  onChange={(value) => {
                    setButtonEnabled(true);
                    setConfigUser((prev) => ({
                      ...prev,
                      kioskButtonCooldown: value,
                    }));
                  }}
                  value={configUser.kioskButtonCooldown}
                />
              </Col>
            </Row>
          </Form.Item>
          <Form.Item
            id="kioskNotificationGracePeriod"
            label="Kiosk Notification Grace Period (seconds)"
            name="kioskNotificationGracePeriod"
          >
            <Row>
              <Col span={colSpanSlider}>
                <Slider
                  max={kioskNotificationGracePeriodMax}
                  min={kioskNotificationGracePeriodMin}
                  onChange={(value) => {
                    setButtonEnabled(true);
                    setConfigUser((prev) => ({
                      ...prev,
                      kioskNotificationGracePeriod: value,
                    }));
                  }}
                  tooltip={{ color: cplNormal, formatter }}
                  value={
                    typeof configUser.kioskNotificationGracePeriod === 'number'
                      ? configUser.kioskNotificationGracePeriod
                      : 0
                  }
                />
              </Col>
              <Col span={colSpanNumber}>
                <InputNumber
                  changeOnWheel
                  className="setting form number"
                  controls
                  max={kioskNotificationGracePeriodMax}
                  min={kioskNotificationGracePeriodMin}
                  onChange={(value) => {
                    setButtonEnabled(true);
                    setConfigUser((prev) => ({
                      ...prev,
                      kioskNotificationGracePeriod: value,
                    }));
                  }}
                  value={configUser.kioskNotificationGracePeriod}
                />
              </Col>
            </Row>
          </Form.Item>
          <Form.Item
            id="tenantAccountApprove"
            label="Require approval for Tenant accounts"
            name="tenantAccountApprove"
            tooltip={{
              arrow: false,
              color: cplNormal,
              title:
                'whether creating Tenant accounts require management approval (always true for Owner accounts)',
            }}
          >
            <Switch
              checked={configUser.tenantAccountApprove}
              checkedChildren={<CheckOutlined />}
              disabled
              onChange={(value) => {
                setButtonEnabled(true);
                setConfigUser((prev) => ({
                  ...prev,
                  tenantAccountApprove: value,
                }));
              }}
              unCheckedChildren={<CloseOutlined />}
            />
          </Form.Item>
          <Form.Item
            id="useTwilioNotification"
            label="Use Twilio Notifications"
            name="useTwilioNotification"
            tooltip={{
              arrow: false,
              color: cplNormal,
              title: '(paid service) used for SMS notifications',
            }}
          >
            <Switch
              checked={configUser.useTwilioNotification}
              checkedChildren={<CheckOutlined />}
              disabled
              onChange={(value) => {
                setButtonEnabled(true);
                setConfigSite((prev) => ({
                  ...prev,
                  useTwilioNotification: value,
                }));
              }}
              unCheckedChildren={<CloseOutlined />}
            />
          </Form.Item>
          <Form.Item
            id="visitorMaxDays"
            label={`Visitor Duration Limit (${visitorMaxDaysMin} - ${visitorMaxDaysMax} days)`}
            name="visitorMaxDays"
          >
            <Row>
              <Col span={colSpanSlider}>
                <Slider
                  max={visitorMaxDaysMax}
                  min={visitorMaxDaysMin}
                  onChange={(value) => {
                    setButtonEnabled(true);
                    setConfigUser((prev) => ({
                      ...prev,
                      visitorMaxDays: value,
                    }));
                  }}
                  tooltip={{ color: cplNormal, formatter }}
                  value={
                    typeof configUser.visitorMaxDays === 'number'
                      ? configUser.visitorMaxDays
                      : 0
                  }
                />
              </Col>
              <Col span={colSpanNumber}>
                <InputNumber
                  changeOnWheel
                  className="setting form number"
                  controls
                  max={visitorMaxDaysMax}
                  min={visitorMaxDaysMin}
                  onChange={(value) => {
                    setButtonEnabled(true);
                    setConfigUser((prev) => ({
                      ...prev,
                      visitorMaxDays: value,
                    }));
                  }}
                  value={configUser.visitorMaxDays}
                />
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Card>
    </>
  );
}

Settings.propTypes = {
  site: PropTypes.shape({ id: PropTypes.string.isRequired }).isRequired,
};
